import { zodResolver } from '@hookform/resolvers/zod';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { Autocomplete, Box, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { ApiError } from '../../../api/client';
import { businessDate, dateTime, money, qty } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useSaveSettings, type ShopSettings } from '../api';
import { applyFieldErrors } from '../lib';
import { FieldGrid, SaveBar, SectionCard } from '../ui';

export const regionalSchema = z.object({
  currencyCode: z.string().regex(/^[A-Z]{3}$/, 'Pick a currency'),
  currencyDecimals: z.number().int().min(0).max(4),
  locale: z
    .string()
    .trim()
    .min(2, 'Pick a language & region')
    .refine((v) => {
      try {
        return Intl.getCanonicalLocales(v).length > 0;
      } catch {
        return false;
      }
    }, 'Not a valid locale, e.g. en-PK'),
  timeZone: z.string().min(1, 'Pick a time zone'),
});
type Values = z.infer<typeof regionalSchema>;
const FIELDS = ['currencyCode', 'currencyDecimals', 'locale', 'timeZone'] as const;

export const COMMON_LOCALES = [
  'en-PK',
  'ur-PK',
  'en-IN',
  'hi-IN',
  'en-AE',
  'ar-AE',
  'ar-SA',
  'en-GB',
  'en-US',
  'en-BD',
  'bn-BD',
  'en-NG',
  'en-KE',
  'en-ZA',
  'fr-FR',
  'de-DE',
  'tr-TR',
  'id-ID',
  'ms-MY',
] as const;

function supported(kind: 'currency' | 'timeZone'): string[] {
  try {
    return Intl.supportedValuesOf(kind);
  } catch {
    return kind === 'currency' ? ['PKR', 'USD', 'AED', 'INR', 'GBP', 'EUR'] : ['Asia/Karachi'];
  }
}

function displayName(type: 'currency' | 'language', code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type }).of(code) ?? code;
  } catch {
    return code;
  }
}

function toValues(s: ShopSettings): Values {
  return {
    currencyCode: s.currencyCode,
    currencyDecimals: s.currencyDecimals,
    locale: s.locale,
    timeZone: s.timeZone,
  };
}

export function RegionalTab({
  settings,
  canManage,
}: {
  settings: ShopSettings;
  canManage: boolean;
}) {
  const notify = useNotify();
  const save = useSaveSettings();
  const currencies = useMemo(() => supported('currency'), []);
  const zones = useMemo(() => supported('timeZone'), []);
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors, isDirty },
  } = useForm<Values>({ resolver: zodResolver(regionalSchema), values: toValues(settings) });

  const onSubmit = handleSubmit(async (v) => {
    try {
      const patch: Partial<Values> = { locale: v.locale.trim(), timeZone: v.timeZone };
      // Currency fields lock after the first posting; only send them when they change.
      if (v.currencyCode !== settings.currencyCode) patch.currencyCode = v.currencyCode;
      if (v.currencyDecimals !== settings.currencyDecimals)
        patch.currencyDecimals = v.currencyDecimals;
      await save.mutateAsync(patch);
      notify.success('Regional settings saved');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'CURRENCY_LOCKED') {
        setError('currencyCode', {
          message: 'The currency can’t change once documents have been posted.',
        });
        return;
      }
      if (!applyFieldErrors(e, FIELDS, setError)) notify.error(e);
    }
  });

  const draft = watch();
  const sample = {
    currencyCode: /^[A-Z]{3}$/.test(draft.currencyCode) ? draft.currencyCode : 'PKR',
    currencyDecimals: draft.currencyDecimals,
    locale: draft.locale || 'en',
    timeZone: zones.includes(draft.timeZone) ? draft.timeZone : 'UTC',
  };
  const ro = !canManage;
  const now = new Date().toISOString();

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <SectionCard
        icon={<LanguageRounded />}
        title="Regional formats"
        description="How money, numbers, dates and times appear across the app and on documents."
      >
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1.4fr) minmax(0,1fr)' },
          }}
        >
          <Stack spacing={2.25}>
            <FieldGrid>
              <Controller
                control={control}
                name="currencyCode"
                render={({ field }) => (
                  <Autocomplete
                    options={currencies}
                    value={field.value}
                    disabled={ro}
                    disableClearable
                    onChange={(_, v) => field.onChange(v)}
                    getOptionLabel={(c) => `${c} — ${displayName('currency', c)}`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Currency"
                        error={Boolean(errors.currencyCode)}
                        helperText={errors.currencyCode?.message}
                      />
                    )}
                  />
                )}
              />
              <Controller
                control={control}
                name="currencyDecimals"
                render={({ field }) => (
                  <TextField
                    select
                    label="Decimal places"
                    disabled={ro}
                    value={field.value}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  >
                    {[0, 1, 2, 3, 4].map((n) => (
                      <MenuItem key={n} value={n}>
                        {n === 0 ? 'None (1,250)' : `${String(n)} (${(1250).toFixed(n)})`}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </FieldGrid>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: -1 }}>
              <LockOutlined sx={{ fontSize: 15, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary">
                Currency and decimals lock once the first document is posted.
              </Typography>
            </Stack>
            <Controller
              control={control}
              name="locale"
              render={({ field }) => (
                <Autocomplete
                  freeSolo
                  options={COMMON_LOCALES}
                  value={field.value}
                  disabled={ro}
                  disableClearable
                  onChange={(_, v) => field.onChange(v)}
                  onInputChange={(_, v) => field.onChange(v)}
                  getOptionLabel={(l) => l}
                  renderOption={(props, l) => {
                    const { key, ...rest } = props;
                    return (
                      <li key={key} {...rest}>
                        <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
                          <Box component="span" sx={{ fontWeight: 600, minWidth: 52 }}>
                            {l}
                          </Box>
                          <Box component="span" sx={{ color: 'text.secondary' }}>
                            {displayName('language', l)}
                          </Box>
                        </Stack>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Language & region"
                      error={Boolean(errors.locale)}
                      helperText={errors.locale?.message ?? displayName('language', field.value)}
                    />
                  )}
                />
              )}
            />
            <Controller
              control={control}
              name="timeZone"
              render={({ field }) => (
                <Autocomplete
                  options={zones}
                  value={field.value}
                  disabled={ro}
                  disableClearable
                  onChange={(_, v) => field.onChange(v)}
                  getOptionLabel={(z) => z.replace(/_/g, ' ')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Time zone"
                      error={Boolean(errors.timeZone)}
                      helperText={
                        errors.timeZone?.message ?? 'Business dates and day-end follow this zone.'
                      }
                    />
                  )}
                />
              )}
            />
          </Stack>

          <Box
            sx={(t) => {
              const p = (t.vars ?? t).palette.primary.main;
              return {
                alignSelf: 'start',
                p: 2.5,
                borderRadius: '16px',
                border: `1px solid color-mix(in srgb, ${p} 16%, transparent)`,
                background: `linear-gradient(160deg, color-mix(in srgb, ${p} 8%, transparent), transparent 70%)`,
              };
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Preview
            </Typography>
            <Typography
              variant="h3"
              className="tabular"
              sx={{ mt: 0.5, mb: 2, wordBreak: 'break-word' }}
            >
              {money(1250000.5, sample)}
            </Typography>
            <Stack spacing={1.25}>
              {[
                ['Quantity', qty(1234.5, sample)],
                ['Business date', businessDate(now.slice(0, 10), sample)],
                ['Date & time', dateTime(now, sample)],
                ['Negative', money(-4500, sample)],
              ].map(([k, v]) => (
                <Stack
                  key={k}
                  direction="row"
                  sx={{ justifyContent: 'space-between', gap: 2, alignItems: 'baseline' }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {k}
                  </Typography>
                  <Typography variant="body2" className="tabular" sx={{ fontWeight: 600 }}>
                    {v}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
      </SectionCard>
      <SaveBar
        dirty={isDirty}
        saving={save.isPending}
        disabled={ro}
        onReset={() => reset(toValues(settings))}
      />
    </form>
  );
}
